import { ForwardedRef, forwardRef, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { FieldProps, useTagFieldRhf } from './hooks/use-tag-field-rhf';
import { TagField } from './tag-field';
import { TagFieldRhfProps } from './tag-field.types';

export const TagFieldRhf = forwardRef(
  (props: TagFieldRhfProps, ref?: ForwardedRef<HTMLDivElement>) => {
    const { rules, ...rest } = props;
    const { control } = useFormContext();

    const renderField = useMemo(() => fieldFactory({ ...props }, ref), [props, ref]);

    return (
      <Controller
        name={rest.name}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        control={control}
        render={renderField}
      />
    );
  }
);

/**
 * In order to be able to use react hooks without breaking
 * the rule of hooks, had to extract the render into a component.
 * This is to ensure correctness of the react-hook-form integration.
 **/
const fieldFactory = (props: TagFieldRhfProps, ref?: ForwardedRef<HTMLDivElement>) => {
  return (fieldProps: FieldProps) => {
    const mergedRefs = useMergeRefs([fieldProps.field.ref, ref]);
    const tagFieldProps = useTagFieldRhf({ ...props, fieldProps });
    return <TagField ref={mergedRefs} {...tagFieldProps} />;
  };
};
